// Copyright (c) 2026 DMG L&D, LLC. All rights reserved.
// Proprietary and confidential. See LICENSE for terms.

/**
 * Compare two text inputs and produce a unified diff.
 *
 * Pure JS — no deps. Uses the classic Myers diff algorithm (simplified
 * line-level implementation) to compute the shortest edit script.
 *
 * Input: two UTF-8 byte arrays (original and modified).
 * Output: UTF-8 bytes of a unified diff string.
 */

import type { Bytes } from '../types.ts';

export interface TextDiffOptions {
  /** Number of context lines around each change (default: 3) */
  context?: number;
  /** Label for the original file (default: 'original') */
  labelA?: string;
  /** Label for the modified file (default: 'modified') */
  labelB?: string;
}

export interface TextDiffResult {
  /** The unified diff as a string */
  diff: string;
  /** Number of added lines */
  additions: number;
  /** Number of removed lines */
  deletions: number;
  /** Whether the inputs are identical */
  identical: boolean;
}

export async function diff(
  a: Bytes,
  b: Bytes,
  opts: TextDiffOptions = {},
): Promise<TextDiffResult> {
  if (!(a instanceof Uint8Array) || !(b instanceof Uint8Array)) {
    throw new Error('text.diff: both inputs must be Uint8Array');
  }

  const textA = new TextDecoder().decode(a);
  const textB = new TextDecoder().decode(b);

  const linesA = textA.split('\n');
  const linesB = textB.split('\n');

  if (textA === textB) {
    return { diff: '', additions: 0, deletions: 0, identical: true };
  }

  const ctx = opts.context ?? 3;
  const labelA = opts.labelA ?? 'original';
  const labelB = opts.labelB ?? 'modified';

  // Compute LCS-based edit operations
  const ops = computeEditOps(linesA, linesB);

  let additions = 0;
  let deletions = 0;
  for (const op of ops) {
    if (op === 'add') additions++;
    if (op === 'del') deletions++;
  }

  // Build unified diff
  const header = `--- ${labelA}\n+++ ${labelB}\n`;
  const hunks = buildHunks(linesA, linesB, ops, ctx);

  return {
    diff: header + hunks,
    additions,
    deletions,
    identical: false,
  };
}

type EditOp = 'keep' | 'add' | 'del';

function computeEditOps(a: string[], b: string[]): EditOp[] {
  const m = a.length;
  const n = b.length;

  // DP LCS table (memory: O(m*n) — fine for text files)
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array<number>(n + 1).fill(0));

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (a[i - 1] === b[j - 1]) {
        dp[i]![j] = dp[i - 1]![j - 1]! + 1;
      } else {
        dp[i]![j] = Math.max(dp[i - 1]![j]!, dp[i]![j - 1]!);
      }
    }
  }

  // Backtrack to get edit ops
  const ops: EditOp[] = [];
  let i = m, j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && a[i - 1] === b[j - 1]) {
      ops.unshift('keep');
      i--; j--;
    } else if (j > 0 && (i === 0 || dp[i]![j - 1]! >= dp[i - 1]![j]!)) {
      ops.unshift('add');
      j--;
    } else {
      ops.unshift('del');
      i--;
    }
  }

  return ops;
}

function buildHunks(
  linesA: string[], linesB: string[], ops: EditOp[], ctx: number,
): string {
  const output: string[] = [];

  // Find change regions
  const changes: number[] = [];
  for (let i = 0; i < ops.length; i++) {
    if (ops[i] !== 'keep') changes.push(i);
  }

  if (changes.length === 0) return '';

  // Group changes into hunks
  const groups: number[][] = [];
  let currentGroup = [changes[0]!];
  for (let c = 1; c < changes.length; c++) {
    if (changes[c]! - changes[c - 1]! <= ctx * 2 + 1) {
      currentGroup.push(changes[c]!);
    } else {
      groups.push(currentGroup);
      currentGroup = [changes[c]!];
    }
  }
  groups.push(currentGroup);

  for (const group of groups) {
    const start = Math.max(0, group[0]! - ctx);
    const end = Math.min(ops.length - 1, group[group.length - 1]! + ctx);

    // Count lines in this hunk
    let hunkStartA = 0, hunkStartB = 0;
    let tmpA = 0, tmpB = 0;
    for (let i = 0; i < ops.length; i++) {
      if (i === start) { hunkStartA = tmpA; hunkStartB = tmpB; }
      if (ops[i] === 'keep' || ops[i] === 'del') tmpA++;
      if (ops[i] === 'keep' || ops[i] === 'add') tmpB++;
    }

    let countA = 0, countB = 0;
    const hunkLines: string[] = [];
    tmpA = 0; tmpB = 0;
    for (let i = 0; i < ops.length; i++) {
      if (ops[i] === 'keep' || ops[i] === 'del') tmpA++;
      if (ops[i] === 'keep' || ops[i] === 'add') tmpB++;
      if (i >= start && i <= end) {
        if (ops[i] === 'keep') {
          hunkLines.push(' ' + linesA[tmpA - 1]);
          countA++; countB++;
        } else if (ops[i] === 'del') {
          hunkLines.push('-' + linesA[tmpA - 1]);
          countA++;
        } else {
          hunkLines.push('+' + linesB[tmpB - 1]);
          countB++;
        }
      }
    }

    output.push(`@@ -${hunkStartA + 1},${countA} +${hunkStartB + 1},${countB} @@`);
    output.push(...hunkLines);
  }

  return output.join('\n') + '\n';
}
